import { useTranslation } from 'react-i18next'
import { softBadgeColors } from '@structural-climbing/ui'
import { disciplineForSection, gradeDisplay } from '@structural-climbing/validation'
import { SurfaceCard } from '../../design/SurfaceCard'
import { useGymGradeConfig } from '../gym/useGymGradeConfig'
import type { SessionTop } from './useMySession'

/**
 * The routes topped today, as colour-and-grade chips: the "Azi" card under the
 * session stats. Nothing at all before the first top.
 */
export function TodayTops({ tops }: { tops: SessionTop[] | null }) {
  const { t } = useTranslation()
  const gradeConfig = useGymGradeConfig()
  if (!tops || tops.length === 0) return null
  const flashes = tops.filter((top) => top.flash).length

  return (
    <SurfaceCard className="flex flex-col gap-2 px-4 py-3">
      <p className="eyebrow text-foreground/40">{t('home.today')}</p>
      <div
        role="list"
        aria-label={t('session.title')}
        className="flex flex-wrap items-center gap-1.5"
      >
        {tops.map((top, i) => {
          const grade =
            top.grade !== null
              ? gradeDisplay(gradeConfig, top.grade, disciplineForSection(top.discipline))
              : null
          const badge = grade ? softBadgeColors(grade.color) : null
          return (
            <span
              role="listitem"
              key={`${top.routeId}-${i}`}
              className="flex items-center gap-1.5 rounded-md bg-[var(--gym-wash)] px-2 py-1"
            >
              <span
                className="h-3 w-3 rounded-full border border-foreground/10"
                style={{ backgroundColor: top.color ?? '#9ca3af' }}
              />
              {grade && badge && (
                <span
                  className="rounded px-1.5 text-xs font-semibold"
                  style={{ backgroundColor: badge.bg, color: badge.fg }}
                >
                  {grade.label}
                </span>
              )}
              {top.flash && (
                <span aria-label={t('session.flash')} className="text-xs">
                  ⚡
                </span>
              )}
            </span>
          )
        })}
        {flashes > 0 && (
          <span className="text-sm text-foreground/50">
            · {t('session.flashes', { count: flashes })}
          </span>
        )}
      </div>
    </SurfaceCard>
  )
}
